/* eslint-disable react/prop-types */
import React, { useId, useMemo } from "react";
import {
  Row,
  Col,
  Card,
  CardBody,
  CardHeader,
  Input,
  Label,
  Alert,
  Button,
  Collapse,
} from "reactstrap";
import PhoneInput from "react-phone-input-2";
import { mediaUrl } from "../../helpers/mediaHelper";
import {
  filenameFromStoredPath,
  normalizeCompanyWebsiteUrl,
} from "./clientProfileFormHelpers";

export function SectionChevron({ open }) {
  return (
    <i
      className={`mdi mdi-chevron-${
        open ? "up" : "down"
      } bg-white text-secondary border border-secondary rounded-circle`}
      style={{
        width: "20px",
        height: "20px",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        fontSize: "12px",
      }}
    />
  );
}

export function SectionSaveFooter({ onSave, isSaving, disabled = false }) {
  return (
    <div className="d-flex justify-content-end mt-3 pt-2 border-top">
      <Button
        type="button"
        color="secondary"
        onClick={onSave}
        disabled={disabled || isSaving}
      >
        {isSaving ? "Saving..." : "Save"}
      </Button>
    </div>
  );
}

export function FieldError({ path, invalid, getAt, errors }) {
  if (!invalid(path)) return null;
  const message = getAt(errors, path);
  if (!message || typeof message !== "string") return null;
  return <div className="text-danger small mt-1">{message}</div>;
}

export function CompanyInformationSection({
  open,
  onToggle,
  values,
  errors,
  touched,
  handleChange,
  handleBlur,
  setFieldValue,
  setFieldTouched,
  invalid,
  getAt,
  onSave,
  isSaving,
}) {
  const onWebsiteBlur = () => {
    const normalized = normalizeCompanyWebsiteUrl(
      values.companyInformation?.companyWebsite
    );
    setFieldValue(
      "companyInformation.companyWebsite",
      normalized || "https://"
    );
    setFieldTouched("companyInformation.companyWebsite", true);
  };

  return (
    <Card className="mb-3">
      <CardHeader
        onClick={onToggle}
        className="d-flex justify-content-between align-items-center"
        style={{ cursor: "pointer", backgroundColor: "#eceeee" }}
      >
        <h6 className="mb-0 fw-bolder">Company Information</h6>
        <SectionChevron open={open} />
      </CardHeader>
      <Collapse isOpen={open}>
        <CardBody style={{ backgroundColor: "#eceeee" }}>
          <Row className="gy-3 gx-4">
            <Col md={6} lg={4}>
              <Label className="fw-bolder">Registered Company Name</Label>
              <Input
                name="companyInformation.registeredCompanyName"
                value={values.companyInformation.registeredCompanyName}
                onChange={handleChange}
                onBlur={handleBlur}
                invalid={invalid("companyInformation.registeredCompanyName")}
              />
              <FieldError
                path="companyInformation.registeredCompanyName"
                invalid={invalid}
                getAt={getAt}
                errors={errors}
              />
            </Col>
            <Col md={6} lg={4}>
              <Label className="fw-bolder">Trading Name</Label>
              <Input
                name="companyInformation.tradingName"
                value={values.companyInformation.tradingName}
                onChange={handleChange}
                onBlur={handleBlur}
              />
            </Col>
            <Col md={6} lg={4}>
              <Label className="fw-bolder">Company Registration Number</Label>
              <Input
                name="companyInformation.companyRegistrationNumber"
                value={values.companyInformation.companyRegistrationNumber}
                onChange={handleChange}
                onBlur={handleBlur}
                invalid={invalid(
                  "companyInformation.companyRegistrationNumber"
                )}
              />
              <FieldError
                path="companyInformation.companyRegistrationNumber"
                invalid={invalid}
                getAt={getAt}
                errors={errors}
              />
            </Col>
            <Col md={6} lg={4}>
              <Label className="fw-bolder">VAT Registration Number</Label>
              <Input
                name="companyInformation.vatRegistrationNumber"
                value={values.companyInformation.vatRegistrationNumber}
                onChange={handleChange}
                onBlur={handleBlur}
                invalid={invalid("companyInformation.vatRegistrationNumber")}
              />
              <FieldError
                path="companyInformation.vatRegistrationNumber"
                invalid={invalid}
                getAt={getAt}
                errors={errors}
              />
            </Col>
            <Col md={6} lg={4}>
              <Label className="fw-bolder">Organisation Type</Label>
              <Input
                type="select"
                name="companyInformation.organisationType"
                value={values.companyInformation.organisationType}
                onChange={handleChange}
                onBlur={handleBlur}
                invalid={invalid("companyInformation.organisationType")}
              >
                {[
                  "Ltd",
                  "PLC",
                  "Charity",
                  "Partnership",
                  "Sole Trader",
                  "Public Sector",
                  "Other",
                ].map((o) => (
                  <option key={o} value={o}>
                    {o}
                  </option>
                ))}
              </Input>
              <FieldError
                path="companyInformation.organisationType"
                invalid={invalid}
                getAt={getAt}
                errors={errors}
              />
            </Col>
            <Col md={6} lg={4}>
              <Label className="fw-bolder">Industry Sector</Label>
              <Input
                name="companyInformation.industrySector"
                value={values.companyInformation.industrySector}
                onChange={handleChange}
                onBlur={handleBlur}
                invalid={invalid("companyInformation.industrySector")}
              />
              <FieldError
                path="companyInformation.industrySector"
                invalid={invalid}
                getAt={getAt}
                errors={errors}
              />
            </Col>
            <Col md={6} lg={6}>
              <Label className="fw-bolder">Registered Office Address</Label>
              <Input
                name="companyInformation.registeredOfficeAddress"
                value={values.companyInformation.registeredOfficeAddress}
                onChange={handleChange}
                onBlur={handleBlur}
                invalid={invalid("companyInformation.registeredOfficeAddress")}
              />
              <FieldError
                path="companyInformation.registeredOfficeAddress"
                invalid={invalid}
                getAt={getAt}
                errors={errors}
              />
            </Col>
            <Col md={6} lg={6}>
              <Label className="fw-bolder">Trading Address</Label>
              <Input
                name="companyInformation.tradingAddress"
                value={values.companyInformation.tradingAddress}
                onChange={handleChange}
                onBlur={handleBlur}
                invalid={invalid("companyInformation.tradingAddress")}
              />
              <FieldError
                path="companyInformation.tradingAddress"
                invalid={invalid}
                getAt={getAt}
                errors={errors}
              />
            </Col>
            <Col md={6} lg={4}>
              <Label className="fw-bolder">Company Website</Label>
              <Input
                name="companyInformation.companyWebsite"
                value={values.companyInformation.companyWebsite}
                onChange={handleChange}
                onBlur={onWebsiteBlur}
                invalid={invalid("companyInformation.companyWebsite")}
              />
              <FieldError
                path="companyInformation.companyWebsite"
                invalid={invalid}
                getAt={getAt}
                errors={errors}
              />
            </Col>
            <Col md={6} lg={4}>
              <Label className="fw-bolder">
                Disability-friendly workplace?
              </Label>
              <Input
                type="select"
                name="companyInformation.isWorkplaceDisabilityFriendly"
                value={values.companyInformation.isWorkplaceDisabilityFriendly}
                onChange={handleChange}
                onBlur={handleBlur}
                invalid={invalid(
                  "companyInformation.isWorkplaceDisabilityFriendly"
                )}
              >
                <option value="">Select</option>
                <option value="yes">Yes</option>
                <option value="no">No</option>
                <option value="unsure">Unsure</option>
              </Input>
              <FieldError
                path="companyInformation.isWorkplaceDisabilityFriendly"
                invalid={invalid}
                getAt={getAt}
                errors={errors}
              />
            </Col>
          </Row>
          <SectionSaveFooter onSave={onSave} isSaving={isSaving} />
        </CardBody>
      </Collapse>
    </Card>
  );
}

export function FinanceBillingSection({
  open,
  onToggle,
  values,
  errors,
  touched,
  handleChange,
  handleBlur,
  setFieldValue,
  setFieldTouched,
  invalid,
  getAt,
  onCopyPrimary,
  onSave,
  isSaving,
}) {
  return (
    <Card className="mb-3">
      <CardHeader
        className="d-flex justify-content-between align-items-center gap-2"
        style={{ backgroundColor: "#eceeee" }}
      >
        <div
          className="d-flex justify-content-between align-items-center flex-grow-1"
          onClick={onToggle}
          onKeyDown={(e) => e.key === "Enter" && onToggle()}
          role="button"
          tabIndex={0}
          style={{ cursor: "pointer" }}
        >
          <h6 className="mb-0 fw-bolder">Finance &amp; Billing</h6>
          <SectionChevron open={open} />
        </div>
        <Button
          type="button"
          color="secondary"
          size="sm"
          outline
          onClick={(e) => {
            e.stopPropagation();
            onCopyPrimary();
          }}
        >
          Copy primary contact
        </Button>
      </CardHeader>
      <Collapse isOpen={open}>
        <CardBody style={{ backgroundColor: "#eceeee" }}>
          <Row className="gy-3 gx-4">
            <Col md={6}>
              <Label className="fw-bolder">Finance contact full name</Label>
              <Input
                name="financeBilling.fullName"
                value={values.financeBilling.fullName}
                onChange={handleChange}
                onBlur={handleBlur}
                invalid={invalid("financeBilling.fullName")}
              />
              <FieldError
                path="financeBilling.fullName"
                invalid={invalid}
                getAt={getAt}
                errors={errors}
              />
            </Col>
            <Col md={6}>
              <Label className="fw-bolder">Finance email</Label>
              <Input
                type="email"
                name="financeBilling.email"
                value={values.financeBilling.email}
                onChange={handleChange}
                onBlur={handleBlur}
                invalid={invalid("financeBilling.email")}
              />
              <FieldError
                path="financeBilling.email"
                invalid={invalid}
                getAt={getAt}
                errors={errors}
              />
            </Col>
            <Col md={6}>
              <Label className="fw-bolder">Finance phone</Label>
              <PhoneInput
                country="gb"
                enableSearch
                countryCodeEditable={false}
                containerClass="w-100"
                inputProps={{ name: "financeBilling.phone" }}
                inputClass={
                  invalid("financeBilling.phone")
                    ? "form-control is-invalid"
                    : "form-control"
                }
                value={values.financeBilling.phone}
                onBlur={() => setFieldTouched("financeBilling.phone", true)}
                onChange={(v) => setFieldValue("financeBilling.phone", v)}
              />
              <FieldError
                path="financeBilling.phone"
                invalid={invalid}
                getAt={getAt}
                errors={errors}
              />
            </Col>
          </Row>
          <SectionSaveFooter onSave={onSave} isSaving={isSaving} />
        </CardBody>
      </Collapse>
    </Card>
  );
}

function ProfileFileInput({
  label,
  accept = ".pdf,image/*",
  multiple = false,
  existingUrl = "",
  existingUrls = [],
  value,
  onChange,
  onBlur,
  invalid: isInvalid,
  error,
  inputId: inputIdProp,
  name,
  path,
  getAt,
  errors,
}) {
  const autoId = useId();
  const inputId = inputIdProp || `profile-file-${autoId.replace(/:/g, "")}`;
  const emptyPlaceholder =
    label || (multiple ? "No files chosen" : "No file chosen");
  const fieldInvalid =
    path && typeof isInvalid === "function"
      ? isInvalid(path)
      : typeof isInvalid === "boolean"
        ? isInvalid
        : false;

  const fileItems = useMemo(() => {
    if (!multiple && value instanceof File) {
      return [
        {
          key: `new-${value.name}-${value.lastModified}`,
          name: value.name,
          isStored: false,
        },
      ];
    }

    const items = [];
    const stored = multiple
      ? (existingUrls || []).filter(Boolean)
      : existingUrl
      ? [existingUrl]
      : [];

    stored.forEach((url) => {
      items.push({
        key: `stored-${url}`,
        name: filenameFromStoredPath(url),
        href: mediaUrl(url),
        isStored: true,
      });
    });

    if (multiple && Array.isArray(value)) {
      value
        .filter((f) => f instanceof File)
        .forEach((f) => {
          items.push({
            key: `new-${f.name}-${f.lastModified}`,
            name: f.name,
            isStored: false,
          });
        });
    }

    return items;
  }, [existingUrl, existingUrls, value, multiple]);

  const displayValue = fileItems.length
    ? fileItems.map((item) => item.name).join(", ")
    : "";

  return (
    <div className="mb-0">
      {label ? <Label className="fw-bolder">{label}</Label> : null}
      <div className="input-group">
        <input
          type="text"
          className={`form-control ${fieldInvalid ? "is-invalid" : ""}`}
          readOnly
          value={displayValue}
          placeholder={emptyPlaceholder}
          aria-label={label || "Selected file"}
        />
        <label
          htmlFor={inputId}
          className="btn btn-outline-secondary mb-0"
          style={{ cursor: "pointer" }}
        >
          {fileItems.length ? "Change" : label || "Browse"}
        </label>
        <input
          id={inputId}
          name={name}
          type="file"
          accept={accept}
          multiple={multiple}
          className="d-none"
          onChange={onChange}
          onBlur={onBlur}
        />
      </div>
      {fileItems.some((item) => item.isStored && item.href) && (
        <ul className="list-unstyled small mb-0 mt-1">
          {fileItems
            .filter((item) => item.isStored && item.href)
            .map((item) => (
              <li key={item.key} className="text-truncate">
                <i className="mdi mdi-file-document-outline me-1" aria-hidden />
                <a href={item.href} target="_blank" rel="noreferrer">
                  {item.name}
                </a>
              </li>
            ))}
        </ul>
      )}
      {path && typeof isInvalid === "function" ? (
        <FieldError
          path={path}
          invalid={isInvalid}
          getAt={getAt}
          errors={errors}
        />
      ) : error ? (
        <div className="text-danger small mt-1">{error}</div>
      ) : null}
    </div>
  );
}

export function InsuranceSection({
  open,
  onToggle,
  values,
  errors,
  touched,
  setFieldValue,
  setFieldTouched,
  invalid,
  getAt,
  onSave,
  isSaving,
}) {
  const ins = values.insurance || {};

  return (
    <Card className="mb-3">
      <CardHeader
        onClick={onToggle}
        className="d-flex justify-content-between align-items-center"
        style={{ cursor: "pointer", backgroundColor: "#eceeee" }}
      >
        <h6 className="mb-0 fw-bolder">Insurance</h6>
        <SectionChevron open={open} />
      </CardHeader>
      <Collapse isOpen={open}>
        <CardBody style={{ backgroundColor: "#eceeee" }}>
          <Row className="gy-3 gx-4">
            <Col md={6}>
              <div className="d-flex align-items-center gap-2 mb-2">
                <Label className="fw-bolder mb-0">Employers liability</Label>
                <span
                  className={`badge rounded-pill ${
                    ins.isEmployersLiabilityVerified
                      ? "bg-success"
                      : "bg-danger"
                  }`}
                >
                  {ins.isEmployersLiabilityVerified ? "Verified" : "Unverified"}
                </span>
              </div>
              {!ins.isEmployersLiabilityVerified &&
                ins.employersLiabilityReason && (
                  <Alert color="warning" className="py-2 px-3 mb-2 small">
                    <strong>Admin note:</strong> {ins.employersLiabilityReason}
                    {ins.employersLiabilityExpiryDate
                      ? ` · Expiry: ${ins.employersLiabilityExpiryDate}`
                      : ""}
                  </Alert>
                )}
              <ProfileFileInput
                label="Employers liability policy"
                name="insurance.employersLiability"
                path="insurance.employersLiability"
                invalid={invalid}
                getAt={getAt}
                errors={errors}
                accept=".pdf,image/*"
                existingUrl={ins.existingEmployersLiability}
                value={ins.employersLiability}
                onChange={(e) => {
                  const f = e.currentTarget.files?.[0];
                  setFieldValue("insurance.employersLiability", f || null);
                }}
                onBlur={() =>
                  setFieldTouched("insurance.employersLiability", true)
                }
              />
            </Col>
            <Col md={6}>
              <div className="d-flex align-items-center gap-2 mb-2">
                <Label className="fw-bolder mb-0">Public liability</Label>
                <span
                  className={`badge rounded-pill ${
                    ins.isPublicLiabilityVerified ? "bg-success" : "bg-danger"
                  }`}
                >
                  {ins.isPublicLiabilityVerified ? "Verified" : "Unverified"}
                </span>
              </div>
              {!ins.isPublicLiabilityVerified && ins.publicLiabilityReason && (
                <Alert color="warning" className="py-2 px-3 mb-2 small">
                  <strong>Admin note:</strong> {ins.publicLiabilityReason}
                  {ins.publicLiabilityExpiryDate
                    ? ` · Expiry: ${ins.publicLiabilityExpiryDate}`
                    : ""}
                </Alert>
              )}
              <ProfileFileInput
                label="Public liability policy"
                name="insurance.publicLiability"
                path="insurance.publicLiability"
                invalid={invalid}
                getAt={getAt}
                errors={errors}
                accept=".pdf,image/*"
                existingUrl={ins.existingPublicLiability}
                value={ins.publicLiability}
                onChange={(e) => {
                  const f = e.currentTarget.files?.[0];
                  setFieldValue("insurance.publicLiability", f || null);
                }}
                onBlur={() =>
                  setFieldTouched("insurance.publicLiability", true)
                }
              />
            </Col>
          </Row>
          <SectionSaveFooter onSave={onSave} isSaving={isSaving} />
        </CardBody>
      </Collapse>
    </Card>
  );
}

function DirectorRow({
  idx,
  dir,
  errors,
  invalid,
  getAt,
  handleChange,
  handleBlur,
  setFieldValue,
  setFieldTouched,
  onRemove,
  canRemove,
}) {
  const base = `ownershipControlDeclaration.directors[${idx}]`;

  return (
    <>
      <Col md={12}>
        <strong>Director {idx + 1}</strong>
      </Col>
      <Col md={12}>
        <Label>Company director full name</Label>
        <Input
          name={`${base}.fullName`}
          value={dir.fullName}
          onChange={handleChange}
          onBlur={handleBlur}
          invalid={invalid(`${base}.fullName`)}
        />
        <FieldError
          path={`${base}.fullName`}
          invalid={invalid}
          getAt={getAt}
          errors={errors}
        />
      </Col>
      <Col md={6}>
        <ProfileFileInput
          label="Passport or driving licence"
          name={`${base}.idFile`}
          path={`${base}.idFile`}
          invalid={invalid}
          getAt={getAt}
          errors={errors}
          accept=".pdf,image/*"
          existingUrl={dir.existingIdUrl}
          value={dir.idFile}
          onChange={(e) => {
            const f = e.currentTarget.files?.[0];
            setFieldValue(`${base}.idFile`, f || null);
          }}
          onBlur={() => setFieldTouched(`${base}.idFile`, true)}
        />
      </Col>
      <Col md={6}>
        <ProfileFileInput
          label="Proof of address (last 3 months)"
          name={`${base}.proofOfAddressFile`}
          path={`${base}.proofOfAddressFile`}
          invalid={invalid}
          getAt={getAt}
          errors={errors}
          accept=".pdf,image/*"
          existingUrl={dir.existingProofUrl}
          value={dir.proofOfAddressFile}
          onChange={(e) => {
            const f = e.currentTarget.files?.[0];
            setFieldValue(`${base}.proofOfAddressFile`, f || null);
          }}
          onBlur={() => setFieldTouched(`${base}.proofOfAddressFile`, true)}
        />
      </Col>
      <Col md={12} className="d-flex justify-content-end">
        <Button
          type="button"
          color="danger"
          outline
          size="sm"
          onClick={onRemove}
          disabled={!canRemove}
        >
          Remove director
        </Button>
      </Col>
      <Col md={12}>
        <hr className="my-2" />
      </Col>
    </>
  );
}

function OwnerRow({
  idx,
  row,
  prefix,
  errors,
  invalid,
  getAt,
  handleChange,
  handleBlur,
  setFieldValue,
  setFieldTouched,
  onRemove,
  canRemove,
}) {
  const base = `ownershipControlDeclaration.${prefix}[${idx}]`;

  return (
    <>
      <Col md={6}>
        <Label>Full name</Label>
        <Input
          name={`${base}.fullName`}
          value={row.fullName}
          onChange={handleChange}
          onBlur={handleBlur}
          invalid={invalid(`${base}.fullName`)}
        />
        <FieldError
          path={`${base}.fullName`}
          invalid={invalid}
          getAt={getAt}
          errors={errors}
        />
      </Col>
      <Col md={6}>
        <Label>Ownership %</Label>
        <Input
          type="number"
          min={25}
          max={100}
          name={`${base}.percentageOfOwnership`}
          value={row.percentageOfOwnership}
          onChange={(e) => {
            const v = e.target.value;
            setFieldValue(
              `${base}.percentageOfOwnership`,
              v === "" ? "" : Number(v)
            );
          }}
          onBlur={handleBlur}
          invalid={invalid(`${base}.percentageOfOwnership`)}
        />
        <FieldError
          path={`${base}.percentageOfOwnership`}
          invalid={invalid}
          getAt={getAt}
          errors={errors}
        />
      </Col>
      <Col md={6}>
        <ProfileFileInput
          label="Passport or driving licence"
          name={`${base}.idFile`}
          path={`${base}.idFile`}
          invalid={invalid}
          getAt={getAt}
          errors={errors}
          accept=".pdf,image/*"
          existingUrl={row.existingIdUrl}
          value={row.idFile}
          onChange={(e) => {
            const f = e.currentTarget.files?.[0];
            setFieldValue(`${base}.idFile`, f || null);
          }}
          onBlur={() => setFieldTouched(`${base}.idFile`, true)}
        />
      </Col>
      <Col md={6}>
        <ProfileFileInput
          label="Proof of address"
          name={`${base}.proofOfAddressFile`}
          path={`${base}.proofOfAddressFile`}
          invalid={invalid}
          getAt={getAt}
          errors={errors}
          accept=".pdf,image/*"
          existingUrl={row.existingProofUrl}
          value={row.proofOfAddressFile}
          onChange={(e) => {
            const f = e.currentTarget.files?.[0];
            setFieldValue(`${base}.proofOfAddressFile`, f || null);
          }}
          onBlur={() => setFieldTouched(`${base}.proofOfAddressFile`, true)}
        />
      </Col>
      <Col md={12} className="d-flex justify-content-end mb-2">
        <Button
          type="button"
          color="danger"
          outline
          size="sm"
          onClick={onRemove}
          disabled={!canRemove}
        >
          Remove owner
        </Button>
      </Col>
    </>
  );
}

export function OwnershipControlSection({
  open,
  onToggle,
  values,
  errors,
  touched,
  handleChange,
  handleBlur,
  setFieldValue,
  setFieldTouched,
  invalid,
  getAt,
  addDirector,
  removeDirector,
  addIndividualOwner,
  removeIndividualOwner,
  addCorporateOwner,
  removeCorporateOwner,
  onOwnershipTypeChange,
  onSave,
  isSaving,
}) {
  const occ = values.ownershipControlDeclaration || {};
  const adminStatus = String(occ.adminStatus || "").toLowerCase();
  const isCleared = adminStatus === "cleared";

  return (
    <Card className="mb-3">
      <CardHeader
        onClick={onToggle}
        className="d-flex justify-content-between align-items-center"
        style={{ cursor: "pointer", backgroundColor: "#eceeee" }}
      >
        <h6 className="mb-0 fw-bolder">Ownership &amp; Control Declaration</h6>
        <SectionChevron open={open} />
      </CardHeader>
      <Collapse isOpen={open}>
        <CardBody style={{ backgroundColor: "#eceeee" }}>
          {occ.adminStatus ? (
            <Row className="mb-3">
              <Col md={12} className="d-flex align-items-center gap-2">
                <span className="fw-bolder">Admin review:</span>
                <span
                  className={`badge rounded-pill ${
                    isCleared ? "bg-success" : "bg-danger"
                  }`}
                >
                  {isCleared ? "Cleared" : "Flagged"}
                </span>
                {!isCleared && occ.adminReason ? (
                  <span className="small text-muted">
                    Reason: {occ.adminReason}
                  </span>
                ) : null}
              </Col>
            </Row>
          ) : null}

          <Row className="gy-3 gx-4">
            <Col md={12}>
              <small className="text-muted d-block mb-2">
                ID must be clear; proof of address within the last 3 months.
              </small>
            </Col>

            {(occ.directors || []).map((dir, idx) => (
              <DirectorRow
                key={`dir-${idx}`}
                idx={idx}
                dir={dir}
                errors={errors}
                invalid={invalid}
                getAt={getAt}
                handleChange={handleChange}
                handleBlur={handleBlur}
                setFieldValue={setFieldValue}
                setFieldTouched={setFieldTouched}
                onRemove={() => removeDirector(idx)}
                canRemove={(occ.directors || []).length > 1}
              />
            ))}

            <Col md={12}>
              <Button
                type="button"
                color="primary"
                outline
                size="sm"
                onClick={addDirector}
              >
                Add another director
              </Button>
            </Col>
            <Col md={12}>
              <FieldError
                path="ownershipControlDeclaration.directors"
                invalid={invalid}
                getAt={getAt}
                errors={errors}
              />
            </Col>

            <Col md={12}>
              <Label className="d-block mb-2">Company ownership type</Label>
              <div className="form-check">
                <Input
                  id="own-individuals-profile"
                  type="radio"
                  className="form-check-input"
                  checked={occ.ownershipCompanyType === "individuals"}
                  onChange={() => onOwnershipTypeChange("individuals")}
                />
                <Label
                  className="form-check-label"
                  htmlFor="own-individuals-profile"
                >
                  Owned by individual(s) only
                </Label>
              </div>
              <div className="form-check">
                <Input
                  id="own-corporate-profile"
                  type="radio"
                  className="form-check-input"
                  checked={occ.ownershipCompanyType === "corporate"}
                  onChange={() => onOwnershipTypeChange("corporate")}
                />
                <Label
                  className="form-check-label"
                  htmlFor="own-corporate-profile"
                >
                  Owned by a corporate entity
                </Label>
              </div>
              <FieldError
                path="ownershipControlDeclaration.ownershipCompanyType"
                invalid={invalid}
                getAt={getAt}
                errors={errors}
              />
            </Col>

            {occ.ownershipCompanyType === "individuals" &&
              (occ.individualOwners || []).map((row, idx) => (
                <OwnerRow
                  key={`ind-${idx}`}
                  idx={idx}
                  row={row}
                  prefix="individualOwners"
                  errors={errors}
                  invalid={invalid}
                  getAt={getAt}
                  handleChange={handleChange}
                  handleBlur={handleBlur}
                  setFieldValue={setFieldValue}
                  setFieldTouched={setFieldTouched}
                  onRemove={() => removeIndividualOwner(idx)}
                  canRemove={(occ.individualOwners || []).length > 1}
                />
              ))}

            {occ.ownershipCompanyType === "individuals" && (
              <Col md={12}>
                <Button
                  type="button"
                  color="primary"
                  outline
                  size="sm"
                  onClick={addIndividualOwner}
                >
                  Add shareholder
                </Button>
              </Col>
            )}

            {occ.ownershipCompanyType === "individuals" && (
              <Col md={12}>
                <FieldError
                  path="ownershipControlDeclaration.individualOwners"
                  invalid={invalid}
                  getAt={getAt}
                  errors={errors}
                />
              </Col>
            )}

            {occ.ownershipCompanyType === "corporate" &&
              (occ.corporateUltimateOwners || []).map((row, idx) => (
                <OwnerRow
                  key={`corp-${idx}`}
                  idx={idx}
                  row={row}
                  prefix="corporateUltimateOwners"
                  errors={errors}
                  invalid={invalid}
                  getAt={getAt}
                  handleChange={handleChange}
                  handleBlur={handleBlur}
                  setFieldValue={setFieldValue}
                  setFieldTouched={setFieldTouched}
                  onRemove={() => removeCorporateOwner(idx)}
                  canRemove={(occ.corporateUltimateOwners || []).length > 1}
                />
              ))}

            {occ.ownershipCompanyType === "corporate" && (
              <Col md={12}>
                <Button
                  type="button"
                  color="primary"
                  outline
                  size="sm"
                  onClick={addCorporateOwner}
                >
                  Add ultimate shareholder
                </Button>
              </Col>
            )}

            {occ.ownershipCompanyType === "corporate" && (
              <Col md={12}>
                <FieldError
                  path="ownershipControlDeclaration.corporateUltimateOwners"
                  invalid={invalid}
                  getAt={getAt}
                  errors={errors}
                />
              </Col>
            )}
          </Row>
          <SectionSaveFooter onSave={onSave} isSaving={isSaving} />
        </CardBody>
      </Collapse>
    </Card>
  );
}
